const STORAGE_PREFIX = 'parties-pagination';
const LOCAL_PARTIES_STORAGE_PREFIX = 'local-parties-pagination';
const USERS_PAGINATION_PREFIX = 'users-pagination';
const DARS_PAGINATION_PREFIX = 'dars-pagination';

export interface PartiesPaginationState {
  pageIndex: number;
  pageSize: number;
  pageTokens: string[];
}

/**
 * Get storage key for a given participant (one key per participant).
 * Key: parties-pagination/participants/{participantName}/parties
 */
export const getPartiesPaginationStorageKey = (participantName: string): string => {
  return `${STORAGE_PREFIX}/participants/${participantName}/parties`;
};

const isValidState = (parsed: unknown): parsed is PartiesPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return (
    typeof s.pageIndex === 'number' &&
    s.pageIndex >= 0 &&
    typeof s.pageSize === 'number' &&
    s.pageSize > 0 &&
    Array.isArray(s.pageTokens) &&
    s.pageTokens.every((t) => typeof t === 'string') &&
    s.pageTokens.length > s.pageIndex
  );
};

/**
 * Load parties pagination state from localStorage
 */
export const loadPartiesPaginationState = (participantName: string): PartiesPaginationState | null => {
  try {
    const key = getPartiesPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load parties pagination state from localStorage:', error);
    return null;
  }
};

/**
 * Save parties pagination state to localStorage.
 * Only tokens needed for the current page are stored.
 */
export const savePartiesPaginationState = (participantName: string, state: PartiesPaginationState): void => {
  try {
    const key = getPartiesPaginationStorageKey(participantName);
    const toStore = {
      ...state,
      pageTokens: state.pageTokens.slice(0, state.pageIndex + 1),
    };
    localStorage.setItem(key, JSON.stringify(toStore));
  } catch (error) {
    console.warn('Failed to save parties pagination state to localStorage:', error);
  }
};

/**
 * Clear parties pagination state for a specific participant
 */
export const clearPartiesPaginationState = (participantName: string): void => {
  try {
    const key = getPartiesPaginationStorageKey(participantName);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear parties pagination state from localStorage:', error);
  }
};

/**
 * Clear all parties pagination states (used during logout)
 */
export const clearAllPartiesPaginationStates = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const prefixes = [STORAGE_PREFIX, LOCAL_PARTIES_STORAGE_PREFIX, USERS_PAGINATION_PREFIX, DARS_PAGINATION_PREFIX];
    keys.forEach((key) => {
      if (prefixes.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear all parties pagination states from localStorage:', error);
  }
};

// -------------------- Local / external parties (client-side pagination) -------------------- //

export type CachedPartiesKind = 'local' | 'external';

export interface LocalPartiesPaginationState {
  pageIndex: number;
  pageSize: number;
}

/** One key per participant and kind: local-parties-pagination/participants/{participantName}/{kind}-parties */
export const getLocalPartiesPaginationStorageKey = (
  participantName: string,
  kind: CachedPartiesKind = 'local',
): string => {
  return `${LOCAL_PARTIES_STORAGE_PREFIX}/participants/${participantName}/${kind}-parties`;
};

const isValidLocalPartiesState = (parsed: unknown): parsed is LocalPartiesPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return typeof s.pageIndex === 'number' && s.pageIndex >= 0 && typeof s.pageSize === 'number' && s.pageSize > 0;
};

export const loadLocalPartiesPaginationState = (
  participantName: string,
  kind: CachedPartiesKind = 'local',
): LocalPartiesPaginationState | null => {
  try {
    const key = getLocalPartiesPaginationStorageKey(participantName, kind);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidLocalPartiesState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load local parties pagination state from localStorage:', error);
    return null;
  }
};

export const saveLocalPartiesPaginationState = (
  participantName: string,
  state: LocalPartiesPaginationState,
  kind: CachedPartiesKind = 'local',
): void => {
  try {
    const key = getLocalPartiesPaginationStorageKey(participantName, kind);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save local parties pagination state to localStorage:', error);
  }
};

// -------------------- Local / external parties list (last response, persisted) -------------------- //

export interface CachedPartiesList {
  parties: string[];
  /** ISO date-time of when the list was received from the API. */
  cachedAt: string;
}

/** One key per participant and kind: local-parties-pagination/participants/{participantName}/{kind}-parties-list */
const getCachedPartiesListStorageKey = (participantName: string, kind: CachedPartiesKind): string => {
  return `${LOCAL_PARTIES_STORAGE_PREFIX}/participants/${participantName}/${kind}-parties-list`;
};

const isValidCachedPartiesList = (parsed: unknown): parsed is CachedPartiesList => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return (
    Array.isArray(s.parties) &&
    s.parties.every((item) => typeof item === 'string') &&
    typeof s.cachedAt === 'string' &&
    !Number.isNaN(Date.parse(s.cachedAt))
  );
};

export const loadCachedPartiesList = (participantName: string, kind: CachedPartiesKind): CachedPartiesList | null => {
  try {
    const key = getCachedPartiesListStorageKey(participantName, kind);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidCachedPartiesList(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load cached parties list from localStorage:', error);
    return null;
  }
};

export const saveCachedPartiesList = (
  participantName: string,
  kind: CachedPartiesKind,
  list: CachedPartiesList,
): void => {
  try {
    const key = getCachedPartiesListStorageKey(participantName, kind);
    localStorage.setItem(key, JSON.stringify(list));
  } catch (error) {
    console.warn('Failed to save cached parties list to localStorage:', error);
  }
};

// -------------------- External parties (kept scan result, persisted) -------------------- //

export interface KeptExternalParties {
  parties: string[];
  /** Cursor from the last response, to read the changes since. */
  cursor: string;
  /** When the list the cursor is based on was scanned (from the API). */
  scannedAt: string;
  /** When the kept cursor was last used, i.e. when the last response was received. */
  cursorUsedAt: string;
}

/** One key per participant: local-parties-pagination/participants/{participantName}/external-parties-kept */
const getKeptExternalPartiesStorageKey = (participantName: string): string => {
  return `${LOCAL_PARTIES_STORAGE_PREFIX}/participants/${participantName}/external-parties-kept`;
};

const isValidDate = (value: unknown) => typeof value === 'string' && !Number.isNaN(new Date(value).getTime());

const isValidKeptExternalParties = (parsed: unknown): parsed is KeptExternalParties => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return (
    Array.isArray(s.parties) &&
    s.parties.every((item) => typeof item === 'string') &&
    typeof s.cursor === 'string' &&
    isValidDate(s.scannedAt) &&
    isValidDate(s.cursorUsedAt)
  );
};

export const loadKeptExternalParties = (participantName: string): KeptExternalParties | null => {
  try {
    const stored = localStorage.getItem(getKeptExternalPartiesStorageKey(participantName));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidKeptExternalParties(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load kept external parties from localStorage:', error);
    return null;
  }
};

export const saveKeptExternalParties = (participantName: string, kept: KeptExternalParties): void => {
  try {
    localStorage.setItem(getKeptExternalPartiesStorageKey(participantName), JSON.stringify(kept));
  } catch (error) {
    console.warn('Failed to save kept external parties to localStorage:', error);
  }
};

// -------------------- Users tab (client-side pagination) -------------------- //

export interface UsersPaginationState {
  pageIndex: number;
  pageSize: number;
}

/** One key per participant: users-pagination/participants/{participantName}/users */
export const getUsersPaginationStorageKey = (participantName: string): string => {
  return `${USERS_PAGINATION_PREFIX}/participants/${participantName}/users`;
};

const isValidUsersPaginationState = (parsed: unknown): parsed is UsersPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return typeof s.pageIndex === 'number' && s.pageIndex >= 0 && typeof s.pageSize === 'number' && s.pageSize > 0;
};

export const loadUsersPaginationState = (participantName: string): UsersPaginationState | null => {
  try {
    const key = getUsersPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidUsersPaginationState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load users pagination state from localStorage:', error);
    return null;
  }
};

export const saveUsersPaginationState = (participantName: string, state: UsersPaginationState): void => {
  try {
    const key = getUsersPaginationStorageKey(participantName);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save users pagination state to localStorage:', error);
  }
};

// -------------------- DAR files tab (client-side pagination) -------------------- //

export interface DarsPaginationState {
  pageIndex: number;
  pageSize: number;
}

/** One key per participant: dars-pagination/participants/{participantName}/dars */
export const getDarsPaginationStorageKey = (participantName: string): string => {
  return `${DARS_PAGINATION_PREFIX}/participants/${participantName}/dars`;
};

const isValidDarsPaginationState = (parsed: unknown): parsed is DarsPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return typeof s.pageIndex === 'number' && s.pageIndex >= 0 && typeof s.pageSize === 'number' && s.pageSize > 0;
};

export const loadDarsPaginationState = (participantName: string): DarsPaginationState | null => {
  try {
    const key = getDarsPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidDarsPaginationState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load dars pagination state from localStorage:', error);
    return null;
  }
};

export const saveDarsPaginationState = (participantName: string, state: DarsPaginationState): void => {
  try {
    const key = getDarsPaginationStorageKey(participantName);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save dars pagination state to localStorage:', error);
  }
};
